import { get, post, remove as del } from '../Services/http';
import { catchError } from './Error.action';
import { loadFavoritesLocally, storeFavoritesLocally, syncFavorites, setFavoritesSource, getFavoritesSource, clearFavoritesSource } from '../Services/Favorites.service';
import { klaviyoFavoritesAddProduct, klaviyoFavoritesRemoveProduct } from '../Services/KlaviyoEvent.service';

export const FAVORITES_LOAD = 'FAVORITES_LOAD';
export const FAVORITES_LOAD_ERROR = 'FAVORITES_LOAD_ERROR';
export const FAVORITES_RECEIVE = 'FAVORITES_RECEIVE';
export const FAVORITES_ADD = 'FAVORITES_ADD';
export const FAVORITES_REMOVE = 'FAVORITES_REMOVE';
export const FAVORITES_TOGGLE = 'FAVORITES_TOGGLE';
export const FAVORITES_SET_LOCAL_MODE = 'FAVORITES_SET_LOCAL_MODE';
export const FAVORITES_SYNC_WITH_SERVER = 'FAVORITES_SYNC_WITH_SERVER';
export const FAVORITES_CLEAR_CLIENT = 'FAVORITES_CLEAR_CLIENT';

const maxFavoriteCount = window.__litium.maxFavoriteCount || 1000;

var loadFavoritesAbortController = new AbortController();

export function enforceMaxFavoriteCount(favorites) {
    if (favorites.length > maxFavoriteCount) {
        return favorites.slice(favorites.length - maxFavoriteCount);
    }
    return favorites;
}

export const load = () => (dispatch, getState) => {
    if (loadFavoritesAbortController) {
        loadFavoritesAbortController.abort();
        loadFavoritesAbortController = new AbortController();
    }

    dispatch({ type: FAVORITES_LOAD });

    let localFavorites = loadFavoritesLocally();
    const favoritesSource = getFavoritesSource();

    localFavorites = enforceMaxFavoriteCount(localFavorites, maxFavoriteCount);
    storeFavoritesLocally(localFavorites);

    return get('/api/favorites', loadFavoritesAbortController)
        .then(response => response.json())
        .then(serverFavorites => {
            if (serverFavorites === null) {
                if (favoritesSource === 'server') {
                    storeFavoritesLocally([]);
                    clearFavoritesSource();
                    dispatch({
                        type: FAVORITES_SET_LOCAL_MODE,
                        payload: { items: [] }
                    });
                } else {
                    dispatch({
                        type: FAVORITES_SET_LOCAL_MODE,
                        payload: { items: localFavorites }
                    });
                }
            } else {
                setFavoritesSource('server');
                dispatch(syncFavoritesWithServer(localFavorites, serverFavorites));
            }
        })
        .catch(error => {
            if (error.name !== 'AbortError') {
                dispatch({
                    type: FAVORITES_SET_LOCAL_MODE,
                    payload: { items: localFavorites }
                });
                dispatch({
                    type: FAVORITES_LOAD_ERROR,
                    payload: { error: error.message }
                });
                dispatch(catchError(error));
            }
        });
};

const syncFavoritesWithServer = (localFavorites, serverFavorites) => (dispatch) => {
    const { missingOnServer, mergedFavorites, needsSync } = syncFavorites(localFavorites, serverFavorites);

    if (!needsSync) {
        dispatch({
            type: FAVORITES_RECEIVE,
            payload: { items: serverFavorites, isLocalMode: false }
        });
        storeFavoritesLocally(serverFavorites);
        setFavoritesSource('server');
        return;
    }

    const syncPromises = missingOnServer.map(item =>
        post('/api/favorites', { item })
            .then(response => response.json())
            .catch(error => {
                console.warn(`Failed to sync favorite item ${item} to server:`, error);
                return null;
            })
    );

    Promise.allSettled(syncPromises).then(results => {
        const serverLists = results
            .map(r => r.value)
            .filter(list => Array.isArray(list));
        const finalList = serverLists.length > 0 ? serverLists[serverLists.length - 1] : mergedFavorites;
        dispatch({
            type: FAVORITES_SYNC_WITH_SERVER,
            payload: { items: finalList, isLocalMode: false }
        });
        storeFavoritesLocally(finalList);
        setFavoritesSource('server');
    });
};

export const add = (item, productData) => (dispatch, getState) => {
    const { favorites } = getState();

    if (favorites.items.includes(item)) {
        return;
    }

    // No need to enforce maxFavoriteCount here; reducer handles it
    dispatch({
        type: FAVORITES_ADD,
        payload: { item }
    });
    klaviyoFavoritesAddProduct(item, productData);

    // Update local storage (rely on reducer to enforce limit)
    const updatedFavorites = [...favorites.items, item];
    storeFavoritesLocally(updatedFavorites);
    setFavoritesSource(favorites.isLocalMode ? 'local' : 'server');

    if (!favorites.isLocalMode) {
        return post('/api/favorites', { item })
            .then(response => response.json())
            .then(serverFavorites => {
                if (Array.isArray(serverFavorites)) {
                    dispatch({
                        type: FAVORITES_RECEIVE,
                        payload: { items: serverFavorites, isLocalMode: false }
                    });
                    storeFavoritesLocally(serverFavorites);
                    setFavoritesSource('server');
                }
            })
            .catch(error => {
                // On server error, revert local change
                dispatch({
                    type: FAVORITES_REMOVE,
                    payload: { item }
                });
                const revertedFavorites = favorites.items.filter(fav => fav !== item);
                storeFavoritesLocally(revertedFavorites);
                setFavoritesSource(favorites.isLocalMode ? 'local' : 'server');
                dispatch(catchError(error));
            });
    }
};

export const remove = (item, productData) => (dispatch, getState) => {
    const { favorites } = getState();

    dispatch({
        type: FAVORITES_REMOVE,
        payload: { item }
    });
    klaviyoFavoritesRemoveProduct(item, productData);

    let updatedFavorites = favorites.items.filter(fav => fav !== item);
    updatedFavorites = enforceMaxFavoriteCount(updatedFavorites, maxFavoriteCount);
    storeFavoritesLocally(updatedFavorites);
    setFavoritesSource(favorites.isLocalMode ? 'local' : 'server');

    // Only sync with server if not in local-only mode
    if (!favorites.isLocalMode) {
        return del(`/api/favorites/${encodeURIComponent(item)}`)
            .catch(error => {
                // On server error, revert local change
                dispatch({
                    type: FAVORITES_ADD,
                    payload: { item }
                });
                const revertedFavorites = enforceMaxFavoriteCount([...favorites.items, item], maxFavoriteCount);
                storeFavoritesLocally(revertedFavorites);
                setFavoritesSource(favorites.isLocalMode ? 'local' : 'server');
                dispatch(catchError(error));
            });
    }
};

export const toggle = (item, productData) => (dispatch, getState) => {
    const { favorites } = getState();
    const isFavorite = favorites.items.includes(item);

    if (isFavorite) {
        dispatch(remove(item, productData));
    } else {
        dispatch(add(item, productData));
    }
};

export const clearClientFavorites = () => (dispatch) => {
    dispatch({ type: FAVORITES_CLEAR_CLIENT });
    storeFavoritesLocally([]);
    clearFavoritesSource();
};
